function escapeLinkLabel(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function decodeLabelEntities(value: string): string {
  return value.replace(/&(?:#(x[0-9a-f]+|[0-9]+)|(amp|quot|apos|lt|gt|nbsp));/gi, (entity, numeric, name) => {
    if (numeric) {
      const point = numeric[0].toLowerCase() === 'x'
        ? Number.parseInt(numeric.slice(1), 16) : Number.parseInt(numeric, 10);
      return point > 0 && point <= 0x10ffff ? String.fromCodePoint(point) : entity;
    }
    return ({ amp: '&', quot: '"', apos: "'", lt: '<', gt: '>', nbsp: ' ' } as Record<string, string>)[name.toLowerCase()];
  });
}

function attribute(attributes: string, name: string): string {
  // Consume complete values so an apparent href inside a quoted title is never
  // mistaken for the anchor's actual destination.
  for (const match of attributes.matchAll(/(?:^|\s)([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g)) {
    if (match[1].toLowerCase() === name) {
      return decodeLabelEntities(match[2] ?? match[3] ?? match[4] ?? '').trim();
    }
  }
  return '';
}

function safeDestinationLabel(href: string): string {
  try {
    const url = new URL(href);
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      // Query parameters and fragments can contain private invitation tokens.
      return `${url.host}${url.pathname === '/' ? '' : url.pathname}`;
    }
    if (url.protocol === 'mailto:') return url.pathname;
  } catch {
    // Relative, missing and malformed destinations cannot supply a label.
  }
  return '';
}

/**
 * Restore a visible label to links whose image was removed by inbound sanitizing.
 * This is presentation only: callers must retain their existing HTML sanitizer.
 * It never changes href, enables images, or fetches a destination.
 */
export function ensureEmailLinkLabels(html: string): string {
  return html.replace(/<a\b((?:"[^"]*"|'[^']*'|[^'">])*)>([\s\S]*?)<\/a\s*>/gi, (anchor, attributes: string, body: string) => {
    const visibleText = decodeLabelEntities(body.replace(/<!--[\s\S]*?-->/g, '').replace(/<[^>]*>/g, ''))
      .replace(/[\s\u200b-\u200d\ufeff]/g, '');
    if (visibleText || /<(?:img|svg|video|audio)\b/i.test(body)) return anchor;
    const destination = safeDestinationLabel(attribute(attributes, 'href'));
    if (!destination) return anchor;
    const label = attribute(attributes, 'title').replace(/[\u200b-\u200d\ufeff]/g, '').trim() || destination;
    return `<a${attributes}>${escapeLinkLabel(label)}</a>`;
  });
}

export interface EmailInlineImageAttachment {
  id?: string | number | null;
  contentId?: string | null;
  contentIds?: string[];
  contentType?: string | null;
  storageKey?: string | null;
}

/** Resolve CID only against the attachments returned by the owner-scoped reader. */
export function resolveEmailCidImages(html: string, messageId?: string | number | null, attachments: EmailInlineImageAttachment[] = []): string {
  const normalizeCid = (value: string) => {
    let cid = value.replace(/^cid:/i, '').trim();
    try { cid = decodeURIComponent(cid); } catch { /* exact opaque ID remains usable */ }
    return cid.replace(/^<|>$/g, '').trim();
  };
  const mapped = html.replace(/<img\b((?:"[^"]*"|'[^']*'|[^'">])*)\/?\s*>/gi, (image, attributes: string) => {
    const src = attribute(attributes, 'src');
    if (!/^cid:/i.test(src)) return image;
    const matches = attachments.filter((attachment) => [attachment.contentId, ...(attachment.contentIds || [])]
      .some((cid) => cid && normalizeCid(cid) === normalizeCid(src))
      && /^(?:image\/(?:png|jpe?g|gif|webp|bmp|avif))(?:;|$)/i.test(attachment.contentType || '')
      && /^[1-9]\d*$/.test(String(attachment.id)) && attachment.storageKey);
    if (matches.length !== 1 || !/^[1-9]\d*$/.test(String(messageId))) {
      return escapeLinkLabel(attribute(attributes, 'alt') || attribute(attributes, 'title'));
    }
    const url = `/api/email/messages/${messageId}/attachments/${matches[0].id}?inline=1`;
    const updated = attributes.replace(/(?:^|\s)([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g,
      (entry, name: string) => name.toLowerCase() === 'src' ? ` src="${url}"` : entry);
    return `<img${updated}>`;
  });
  return ensureEmailLinkLabels(mapped);
}
